import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #viewportObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
    this.#observeViewport(videoElement);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#viewportObserver?.disconnect();
    this.#viewportObserver = null;
  }

  /**
   * @param {HTMLVideoElement} videoElement
   */
  #observeViewport(videoElement) {
    if (typeof IntersectionObserver === 'undefined') return;

    this.#viewportObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (visible) {
          videoElement.play().catch(() => {});
        } else {
          videoElement.pause();
        }
      },
      { threshold: 0.15 }
    );
    this.#viewportObserver.observe(this);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
